import { AxiosResponse } from 'axios';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';

import { DataSis } from '../domain/sis/types';

import {
  DataSisActionCreators,
  DataSisActionTypes,
  FetchDataSisAction,
} from '../redux/server/dataSis';
import {
  selectSisTableHeaderSubMenuMultiFilterAction,
  selectSisTableHeaderSubMenuMultiFilterSelectOptionsAction,
  sisTableHeaderSubMenuMultiFilterSelector,
} from '../redux/ui/sisSettings';
import { Api, buildConfig } from '../utils/api';
import { sortBy as lodashSortBy } from '../utils/sortBy';
import {
  SIS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
  selectedValuesWithinOptions,
  typeMultiFilterSelectOptions,
} from '../utils/tableHeaderSubMenuMultiFilter';
import { handleErrorSaga } from './errorSaga';
import { sisTypeNamesForTypeMultiFilterSaga } from './sisTypeNamesForTypeMultiFilterSaga';

/**
 * dataSisのデータを取得する
 */
export function* fetchDataSisSaga(api: Api, action: FetchDataSisAction) {
  try {
    yield put(DataSisActionCreators.fetchDataSisRequestAction());

    const response: AxiosResponse<DataSis> = yield call(
      api.get,
      '/data/sis',
      buildConfig(action.payload.params)
    );

    const dataSis = response.data;

    yield put(DataSisActionCreators.fetchDataSisSuccessAction(dataSis));

    // マルチフィルターを表示するカラムの列インデックスを求める
    const columnIndexMap =
      SIS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER.reduce(
        (acc, code) => {
          const idx = dataSis.data.columns.findIndex((c) => c.code === code);
          if (idx >= 0) {
            acc[code] = idx;
          }
          return acc;
        },
        {} as Record<string, number>
      );

    /**
     * 並び替え関数
     * (現在使用していない。将来的に稼動貢献・総合貢献以外にマルチフィルタを適用する場合に使用)
     * @param code カラムごとにswitchで処理を分ける場合に使用
     * @param values 要素
     */
    const processColumnValues = (code: string, values: string[]): string[] => {
      return lodashSortBy(
        values.filter((v) => v !== '-'),
        [
          // 数字を含むものは数字順、それ以外は辞書順
          (v) => (typeof v === 'string' && /\d/.test(v) ? 0 : 1),
          (v) => {
            if (typeof v === 'string' && /\d/.test(v)) {
              // 文字列中の最初の数字を抜き出して数値化
              const match = v.match(/(\d+)/);
              return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
            }
            return v;
          },
        ]
      );
    };

    const sisTypeNames: string[] =
      columnIndexMap.type === undefined
        ? []
        : yield call(sisTypeNamesForTypeMultiFilterSaga);

    // マルチセレクトフィルタ用の選択肢オブジェクトをカラムごとに作成
    const newOptions =
      SIS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER.reduce<
        Record<string, string[]>
      >((acc, code) => {
        const index = columnIndexMap[code];
        if (index === undefined) {
          acc[code] = [];
          return acc;
        }

        const values = dataSis.data.rows
          .map((row) => row.data.at(index)?.value)
          .filter((v): v is string => typeof v === 'string');

        if (code === 'sisKadoKokenWeek' || code === 'sisSogoKokenWeek') {
          // SIS稼動貢献週、SIS総合貢献週は「(継)」「(終)」の有無で選択肢を生成
          const opts: string[] = [];
          if (values.some((v) => v.includes('(継)'))) {
            opts.push('継続');
          }
          if (values.some((v) => v.includes('(終)'))) {
            opts.push('終了');
          }
          acc[code] = opts;
        } else if (code === 'type') {
          acc[code] = typeMultiFilterSelectOptions(
            sisTypeNames,
            Array.from(new Set(values))
          );
        } else {
          const uniqueValues = Array.from(new Set(values));
          acc[code] = processColumnValues(code, uniqueValues);
        }

        return acc;
      }, {});

    yield put(
      selectSisTableHeaderSubMenuMultiFilterSelectOptionsAction(newOptions)
    );

    const currentFilter: ReturnType<
      typeof sisTableHeaderSubMenuMultiFilterSelector
    > = yield select(sisTableHeaderSubMenuMultiFilterSelector);
    const selectedType = currentFilter?.type ?? [];
    const nextType = selectedValuesWithinOptions(
      selectedType,
      newOptions.type ?? []
    );
    if (selectedType.length !== nextType.length) {
      yield put(
        selectSisTableHeaderSubMenuMultiFilterAction({ type: nextType })
      );
    }
  } catch (error: unknown) {
    yield put(DataSisActionCreators.renewDataSis());
    yield fork(handleErrorSaga, error);
  }
}

function* handleFetchDataSisSaga(api: Api) {
  yield takeEvery(DataSisActionTypes.FETCH_DATA_SIS, fetchDataSisSaga, api);
}

export function* dataSisSaga(context: { api: Api }) {
  yield fork(handleFetchDataSisSaga, context.api);
}
